import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, ChevronDown, Loader2, EditIcon, Trash2, Database, Copy } from 'lucide-react';
import { getTenantCollections, getAgentsForTenant, createAgent, registerAgent, deleteAgent, generateLocalPackage } from '../services/apiClient';
import { getErrorMessage } from '../utils/errors';
import { getAuthHeaders } from '../utils/getAuthHeaders';
import CollectionForm from '../components/CollectionForm';
import { identifyUser, trackEvent } from '../utils/highlight';
// Removed ApiForm and related apiClient imports

import { Button } from "../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible"

export default function Account() {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [collections, setCollections] = useState([]);
  const [showNewCollectionForm, setShowNewCollectionForm] = useState(false);
  const [showEditCollectionForm, setShowEditCollectionForm] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  // Removed showNewApiForm state
  const [error, setError] = useState(null);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  
  // Agent management state
  const [agents, setAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);
  const [registrationDetails, setRegistrationDetails] = useState({});
  const [registeringAgentId, setRegisteringAgentId] = useState(null);
  const [agentError, setAgentError] = useState(null);
  const [downloadingAgentId, setDownloadingAgentId] = useState(null);
  
  const navigate = useNavigate();
  // Removed editingApi state

  const tenantId = user?.['https://tenxer.ai/tenant_id'];

  // Identify user with Highlight.io when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      identifyUser(user, {
        tenant_id: tenantId,
        environment: process.env.NODE_ENV,
        timestamp: new Date().toISOString(),
      });
      
      // Track page visit
      trackEvent('account_page_viewed', {
        tenant_id: tenantId,
        collections_count: collections.length,
        agents_count: agents.length,
      });
    }
  }, [isAuthenticated, user, tenantId, collections.length, agents.length]);

  useEffect(() => {
    const loadCollections = async () => {
      if (!tenantId) return;
      
      setIsLoadingCollections(true);
      setError(null);
      
      try {
        // Get token from Auth0
        const token = await getAccessTokenSilently();
        const data = await getTenantCollections(tenantId, token);
        setCollections(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(getErrorMessage(err));
        setCollections([]);
      } finally {
        setIsLoadingCollections(false);
      }
    };

    loadCollections();
  }, [tenantId, getAccessTokenSilently]);


  // Load agents when tenantId is available
  useEffect(() => {
    const loadAgents = async () => {
      if (!tenantId) return;
      
      setIsLoadingAgents(true);
      setAgentError(null);
      
      try {
        const token = await getAccessTokenSilently();
        const headers = getAuthHeaders(token);
        const data = await getAgentsForTenant(tenantId, headers);
        const agentsList = Array.isArray(data) ? data : [];
        setAgents(agentsList);
      } catch (err) {
        setAgentError(getErrorMessage(err));
        setAgents([]);
      } finally {
        setIsLoadingAgents(false);
      }
    };

    loadAgents();
  }, [tenantId, getAccessTokenSilently]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-[350px]">
          <CardContent className="flex flex-col items-center justify-center p-6">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-4" />
            <CardTitle className="text-xl font-semibold mb-2">Loading...</CardTitle>
            <CardDescription>Please wait while we load your account information</CardDescription>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-[350px]">
          <CardContent className="flex flex-col items-center justify-center p-6">
            <XCircle className="h-8 w-8 text-red-500 mb-4" />
            <CardTitle className="text-xl font-semibold">Not authenticated</CardTitle>
          </CardContent>
        </Card>
      </div>
    );
  }


  const handleNewCollectionSubmit = async (newCollection) => {
    try {
      // Get token from Auth0
      const token = await getAccessTokenSilently();
      const updatedCollections = await getTenantCollections(tenantId, token);
      setCollections(updatedCollections);
      setShowNewCollectionForm(false);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleEditCollection = (collection) => {
    // Format collection for the form
    setEditingCollection({
      id: collection.collection_id,
      name: collection.collection_name,
      description: collection.collection_description || '',
      authHeaders: collection.auth_headers || {}
    });
    setShowEditCollectionForm(true);
  };
  
  const handleEditCollectionSubmit = async (updatedCollection) => {
    try {
      // Get token from Auth0
      const token = await getAccessTokenSilently();
      const updatedCollections = await getTenantCollections(tenantId, token);
      setCollections(updatedCollections);
      setShowEditCollectionForm(false);
      setEditingCollection(null);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  // Removed handleApiSubmit, handleEditApi, handleDeleteApi functions

  const handleLaunchChat = (collectionId) => {
    navigate(`/chat/${collectionId}`);
  };

  // Agent management functions
  const handleCreateAgent = async () => {
    setIsCreatingAgent(true);
    setAgentError(null);
    try {
      const token = await getAccessTokenSilently();
      const headers = getAuthHeaders(token);
      await createAgent(tenantId, headers);
      
      // Reload agents list
      const data = await getAgentsForTenant(tenantId, headers);
      setAgents(Array.isArray(data) ? data : []);
    } catch (err) {
      setAgentError(getErrorMessage(err));
    } finally {
      setIsCreatingAgent(false);
    }
  };

  const handleRegisterAgent = async (agentId) => {
    setRegisteringAgentId(agentId);
    setAgentError(null);
    try {
      const token = await getAccessTokenSilently();
      const headers = getAuthHeaders(token);
      const regInfo = await registerAgent(agentId, headers);
      setRegistrationDetails(prev => ({ ...prev, [agentId]: regInfo }));
      
      // Reload agents to show updated status
      const data = await getAgentsForTenant(tenantId, headers);
      setAgents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Registration error:', err);
      setAgentError(`Registration failed: ${getErrorMessage(err)}`);
    } finally {
      setRegisteringAgentId(null);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = await getAccessTokenSilently();
      const headers = getAuthHeaders(token);
      await deleteAgent(tenantId, agentId, headers);
      
      // Remove from local state
      setAgents(prev => prev.filter(agent => agent.agent_id !== agentId));
      
      // Also remove any registration details
      setRegistrationDetails(prev => {
        const updated = { ...prev };
        delete updated[agentId];
        return updated;
      });
    } catch (err) {
      setAgentError(`Delete failed: ${getErrorMessage(err)}`);
    }
  };

  const handleDownloadAgentPackage = async (agentId) => {
    if (!tenantId) {
      setAgentError('Tenant ID missing; refresh and try again.');
      return;
    }

    try {
      setDownloadingAgentId(agentId);
      setAgentError(null);
      const token = await getAccessTokenSilently();
      const headers = getAuthHeaders(token);
      const packageBlob = await generateLocalPackage(tenantId, agentId, {}, headers);
      const url = window.URL.createObjectURL(packageBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tenxer-agent-${agentId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Agent package download error:', err);
      setAgentError(`Package download failed: ${getErrorMessage(err)}`);
    } finally {
      setDownloadingAgentId(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };


  // Local deployment handlers
  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-1">
        <section className="w-full py-4 md:py-8 lg:py-16 xl:py-24">
          <div className="container px-8 md:px-10 mx-auto max-w-7xl">
            <div className="flex flex-col space-y-4">
      <h1 className="text-4xl font-bold mb-8">Your Account</h1>
      
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="bg-transparent">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="collections">Collections</TabsTrigger>
          <TabsTrigger value="agents">Database Agents</TabsTrigger>
        </TabsList>
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Name:</strong> {user.name}</p>
                <p><strong>Member Since:</strong> January 1, 2023</p>
                <p><strong>Tenant Id:</strong> {tenantId}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="collections">
          <Card>
            <CardHeader>
              <CardTitle>Your Collections</CardTitle>
              <CardDescription>Manage your collections and APIs</CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Alert variant="default" className="mb-4">
                <AlertTitle>Getting Started</AlertTitle>
                <AlertDescription>
                  Create a collection to organize your APIs and start chatting. Each collection can contain multiple APIs that you want to interact with.
                  Once you've created a collection, you can add APIs to it and launch the chat interface to start making requests. 
                  You can also add optional headers to each API, which will be added to every request for that API. Collection level headers will be added to every request for every API in the collection 
                  (note that collection level headers as a feature are still in development).
                </AlertDescription>
              </Alert>

              <div className="flex space-x-4 mb-4">
                <Dialog open={showNewCollectionForm} onOpenChange={setShowNewCollectionForm}>
                  <DialogTrigger asChild>
                    <Button>Create New Collection</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Collection</DialogTitle>
                      <DialogDescription>Fill in the details to create a new collection.</DialogDescription>
                    </DialogHeader>
                    <CollectionForm
                      tenantId={tenantId}
                      getAccessTokenSilently={getAccessTokenSilently}
                      onSubmit={handleNewCollectionSubmit}
                      onCancel={() => setShowNewCollectionForm(false)}
                      isEditing={false}
                    />
                  </DialogContent>
                </Dialog>

                {/* Edit Collection Dialog */}
                <Dialog open={showEditCollectionForm} onOpenChange={(open) => {
                  setShowEditCollectionForm(open);
                  if (!open) setEditingCollection(null);
                }}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Edit Collection</DialogTitle>
                      <DialogDescription>Update the collection details.</DialogDescription>
                    </DialogHeader>
                    <CollectionForm
                      tenantId={tenantId}
                      getAccessTokenSilently={getAccessTokenSilently}
                      onSubmit={handleEditCollectionSubmit}
                      onCancel={() => {
                        setShowEditCollectionForm(false);
                        setEditingCollection(null);
                      }}
                      initialCollection={editingCollection}
                      isEditing={true}
                    />
                  </DialogContent>
                </Dialog>

                {/* Removed Dialog and Button for Create New API */}
              </div>

              {isLoadingCollections ? (
                <div className="text-center py-4">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                  <p className="mt-2">Loading collections...</p>
                </div>
              ) : collections.length === 0 ? (
                <p className="text-center py-4 text-gray-500">
                  No collections found. Create your first collection to get started!
                </p>
              ) : (
                <div className="space-y-4">
                  {collections.map((collection) => (
                    <Collapsible key={collection.collection_id}>
                      <div className="flex items-center justify-between">
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" className="w-[calc(100%-180px)] justify-between">
                            <span>{collection.collection_name}</span>
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="ml-4" // Added left margin for spacing
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent collapse toggle
                              handleEditCollection(collection);
                            }}
                          >
                            <EditIcon className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleLaunchChat(collection.collection_id)}
                          >
                            Launch Chat
                          </Button>
                        </div>
                      </div>
                      <CollapsibleContent className="mt-2 p-4 bg-muted rounded-md">
                        <p><strong>Description:</strong> {collection.collection_description}</p>
                        {/* Removed API listing and management buttons */}
                        <p className="text-sm text-gray-500 mt-2">
                          Manage Data Sources (APIs and Databases) within the Chat Page sidebar for this collection.
                        </p>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="agents">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Database Agents
              </CardTitle>
              <CardDescription>
                Manage database agents that connect your SQL databases to Tenxer. Agents run in your infrastructure and provide secure, read-only access to your databases.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {agentError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{agentError}</AlertDescription>
                </Alert>
              )}

              <Alert variant="default" className="mb-4">
                <AlertTitle>Getting Started with Database Agents</AlertTitle>
                <AlertDescription>
                  1. Create a new agent to get started<br/>
                  2. Register the agent to mark it ready for queue polling<br/>
                  3. Deploy the Dockerized agent using the provided environment variables<br/>
                  4. Add database data sources that point at this agent<br/>
                  <br/>
                  Agents poll the Tenxer backend over HTTPS (port 443) so they work behind firewalls.
                </AlertDescription>
              </Alert>

              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Your Agents</h3>
                <Button 
                  onClick={handleCreateAgent} 
                  disabled={isCreatingAgent || isLoadingAgents}
                  className="flex items-center gap-2"
                >
                  {isCreatingAgent ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Database className="h-4 w-4" />
                      Create New Agent
                    </>
                  )}
                </Button>
              </div>

              {isLoadingAgents ? (
                <div className="flex justify-center items-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <span className="ml-2">Loading agents...</span>
                </div>
              ) : agents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No agents created yet</p>
                  <p>Create your first database agent to get started with SQL data sources.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {agents.map((agent) => {
                    const registration = registrationDetails[agent.agent_id];
                    const envVars = registration?.env || {};
                    const orderedKeys = ['TENANT_ID', 'AGENT_ID', 'BACKEND_API_URL', 'AGENT_SHARED_SECRET'];
                    const orderedEntries = [
                      ...orderedKeys
                        .filter((key) => envVars[key] !== undefined)
                        .map((key) => [key, envVars[key]]),
                      ...Object.entries(envVars).filter(([key]) => !orderedKeys.includes(key)),
                    ];
                    const envSnippet = orderedEntries
                      .map(([key, value]) => `${key}=${value ?? ''}`)
                      .join('\n');
                    const sharedSecretValue = envVars.AGENT_SHARED_SECRET;
                    const sharedSecretProvided = Boolean(sharedSecretValue && sharedSecretValue !== '<set-agent-shared-secret>');

                    return (
                      <div key={agent.agent_id} className="border rounded-lg p-4 bg-white shadow-sm">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2 flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-lg">Agent {agent.agent_id}</h4>
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  agent.status === 'REGISTERED'
                                    ? 'bg-green-100 text-green-800'
                                    : agent.status === 'CREATED'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {agent.status || 'UNKNOWN'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>
                                <strong>Agent ID:</strong>{' '}
                                <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{agent.agent_id}</code>
                              </p>
                              <p>
                                <strong>Created:</strong> {new Date(agent.created_at).toLocaleString()}
                              </p>
                              {agent.last_seen && (
                                <p>
                                  <strong>Last Seen:</strong> {new Date(agent.last_seen).toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {agent.status === 'CREATED' && (
                              <Button
                                onClick={() => handleRegisterAgent(agent.agent_id)}
                                disabled={registeringAgentId === agent.agent_id}
                                variant="outline"
                                size="sm"
                              >
                                {registeringAgentId === agent.agent_id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                    Registering...
                                  </>
                                ) : (
                                  'Register'
                                )}
                              </Button>
                            )}
                            <Button
                              onClick={() => handleDeleteAgent(agent.agent_id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {registration && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                            <h5 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                              <CheckCircle className="h-4 w-4" />
                              Agent Ready For Deployment
                            </h5>

                            <p className="text-sm text-gray-700 mb-3">
                              Configure the on-prem agent container with the following environment variables:
                            </p>

                            <pre className="bg-white border rounded p-3 font-mono text-xs overflow-x-auto whitespace-pre-wrap">
                              {envSnippet}
                            </pre>

                            <div className="mt-2 flex flex-wrap gap-2">
                              <Button onClick={() => copyToClipboard(envSnippet)} variant="outline" size="sm">
                                <Copy className="h-3 w-3 mr-1" /> Copy env snippet
                              </Button>
                              <Button
                                onClick={() => handleDownloadAgentPackage(agent.agent_id)}
                                disabled={downloadingAgentId === agent.agent_id}
                                size="sm"
                              >
                                {downloadingAgentId === agent.agent_id ? (
                                  <>
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                    Preparing package…
                                  </>
                                ) : (
                                  'Download agent package'
                                )}
                              </Button>
                            </div>

                            {registration.message && (
                              <p className="text-xs text-gray-600 mt-2">{registration.message}</p>
                            )}

                            <p className="text-xs text-gray-500 mt-3">
                              Tip: copy these values into your agent's .env file before running the container.
                            </p>

                            {sharedSecretProvided ? (
                              <p className="text-xs text-amber-600 mt-2">
                                Treat <code>AGENT_SHARED_SECRET</code> like any password. Rotate it if you suspect disclosure.
                              </p>
                            ) : (
                              <p className="text-xs text-amber-600 mt-2">
                                Set <code>AGENT_SHARED_SECRET</code> on the backend before starting the agent to enable shared-secret authentication.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}


              {/* Local Deployment Dialog */}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
